<template>
  <FloatingCard class="stack" :feeling="feeling">
    <TextField
      v-model="documentManagerToken"
      type="text"
      label="Token do Document Manageru"
      :helper="helper"
      :feeling="feeling"
      id="document-manager-token"
    />
    <div>
      <ButtonComponent
        :label="saveButtonLabel"
        :disabled="saveButtonDisabled"
        @click="updateToken"
      />
      <template v-if="status === 'token-exists' || status === 'token-saved'">
        nebo <a href="/" @click.prevent="removeToken">smazat</a>.
      </template>
    </div>
  </FloatingCard>
</template>

<script>
import jwtDecode from 'jwt-decode';

import ButtonComponent from '@/components/ButtonComponent';
import TextField from '@/components/TextField';
import FloatingCard from '@/components/FloatingCard';

import Flexi from '@/mixins/Flexi';

export default {
  name: 'FlexiTokenSetupCard',
  components: {
    ButtonComponent,
    TextField,
    FloatingCard,
  },
  mixins: [
    Flexi,
  ],
  props: {
    apiURL: {
      type: String,
    },
    username: {
      type: String,
    },
    password: {
      type: String,
    },
    token: {
      type: String,
    },
    company: {
      type: String,
    },
  },
  data() {
    return {
      status: null,
      documentManagerToken: null,
    };
  },
  computed: {
    feeling() {
      switch (this.status) {
        case 'checking-token':
          return 'pending';
        case 'token-exists':
          return 'positive';
        case 'token-missing':
          return null;
        case 'token-invalid':
          return 'negative';
        case 'token-expired':
          return 'negative';
        case 'updating-token':
          return 'pending';
        case 'token-saved':
          return 'positive';
        case 'removing-token':
          return 'pending';
        case 'token-removed':
          return null;
        case 'something-wrong':
          return 'negative';
        default:
          return null;
      }
    },
    helper() {
      switch (this.status) {
        case 'checking-token':
          return 'Načítám token';
        case 'token-exists':
          return 'Token je již uložen';
        case 'token-missing':
          return 'Token si můžete vygenerovat v Můj profil → Externí aplikace';
        case 'token-invalid':
          return 'Token není platný. Vygenerujte si prosím nový v Můj profil → Externí aplikace.';
        case 'token-expired':
          return 'Platnost tokenu vypršela. Vygenerujte si prosím nový v Můj profil → Externí aplikace.';
        case 'updating-token':
          return 'Ukládám token';
        case 'token-saved':
          return 'Token uložen';
        case 'removing-token':
          return 'Mažu token';
        case 'token-removed':
          return 'Token smazán, můžete si uložit nový';
        case 'something-wrong':
          return 'Něco se pokazilo, ozvěte se nám prosím';
        default:
          return null;
      }
    },
    saveButtonDisabled() {
      return !this.documentManagerToken;
    },
    saveButtonLabel() {
      switch (this.status) {
        case 'token-exists':
          return 'Aktualizovat';
        case 'token-saved':
          return 'Aktualizovat';
        default:
          return 'Uložit';
      }
    },
  },
  watch: {
    status() {
      this.$emit('status-change', { status: this.status, data: this.$data });
      this.validateToken(this.documentManagerToken);
    },
    company() {
      this.retrieveToken();
    },
    documentManagerToken() {
      this.validateToken(this.documentManagerToken);
    },
  },
  methods: {
    async retrieveToken() {
      try {
        this.status = 'checking-token';
        const remoteToken = await this.retrieveRemoteToken();
        if (remoteToken) {
          this.documentManagerToken = remoteToken;
          this.status = 'token-exists';
        } else {
          this.documentManagerToken = null;
          this.status = 'token-missing';
        }
      } catch (error) {
        if (error.response.status === 400) {
          const localToken = this.retrieveLocalToken();
          if (localToken) {
            this.documentManagerToken = localToken;
            this.status = 'token-exists';
          } else {
            this.documentManagerToken = null;
            this.status = 'token-missing';
          }
        } else {
          this.status = 'something-wrong';
          throw error;
        }
      }
    },
    async updateToken() {
      try {
        this.status = 'updating-token';
        await this.updateRemoteToken();
        this.status = 'token-saved';
      } catch (error) {
        if (error.response.status === 400) {
          this.updateLocalToken();
          this.status = 'token-saved';
        } else {
          this.status = 'something-wrong';
          throw error;
        }
      }
    },
    async removeToken() {
      try {
        this.status = 'removing-token';
        await this.removeRemoteToken();
        this.documentManagerToken = null;
        this.status = 'token-removed';
      } catch (error) {
        if (error.response.status === 400) {
          this.removeLocalToken();
          this.documentManagerToken = null;
          this.status = 'token-removed';
        } else {
          this.status = 'something-wrong';
          throw error;
        }
      }
    },
    async retrieveRemoteToken() {
      const token = await this.$_Flexi_retrieveToken({
        apiURL: this.apiURL,
        username: this.username,
        password: this.password,
        token: this.token,
        company: this.company,
      });
      return token;
    },
    async updateRemoteToken() {
      await this.$_Flexi_addToken({
        apiURL: this.apiURL,
        username: this.username,
        password: this.password,
        token: this.token,
        company: this.company,
        documentManagerToken: this.documentManagerToken,
      });
    },
    async removeRemoteToken() {
      await this.$_Flexi_removeToken({
        apiURL: this.apiURL,
        username: this.username,
        password: this.password,
        token: this.token,
        company: this.company,
      });
    },
    retrieveLocalToken() {
      if (localStorage.documentManagerToken) {
        return JSON.parse(localStorage.documentManagerToken)[this.company];
      }
      return false;
    },
    updateLocalToken() {
      let tokens;
      if (localStorage.documentManagerToken) {
        tokens = JSON.parse(localStorage.documentManagerToken);
        tokens[this.company] = this.documentManagerToken;
      } else {
        tokens = { [this.company]: this.documentManagerToken };
      }
      localStorage.documentManagerToken = JSON.stringify(tokens);
    },
    removeLocalToken() {
      const tokens = JSON.parse(localStorage.documentManagerToken);
      delete tokens[this.company];
      if (Object.keys(tokens).length === 0 && tokens.constructor === Object) {
        localStorage.removeItem('documentManagerToken');
      } else {
        localStorage.documentManagerToken = JSON.stringify(tokens);
      }
    },
    validateToken(token) {
      if (this.documentManagerToken) {
        try {
          const { exp } = jwtDecode(token);
          if (exp < (+new Date() / 1000)) {
            this.status = 'token-expired';
          }
        } catch (error) {
          this.status = 'token-invalid';
        }
      } else {
        this.status = 'token-missing';
      }
    },
  },
  mounted() {
    this.retrieveToken();
  },
};
</script>
